import { useSelectedOrganizationName } from '@client/store/organization';
import { Box, ListItemButton, ListItemText, Tooltip } from '@mui/material';
import { getEPDConversionFactor } from '@shared/helpers/conversion-factors.helpers';
import { isReadonlyBySelectedOrganization } from '@shared/helpers/organization.helpers';
import { IProduct, ProductSources } from '@shared/models/product.model';
import { QuantityUnit } from '@shared/models/unit.model';
import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  AutoSizer,
  type AutoSizerChildProps,
} from 'react-virtualized-auto-sizer';
import { List, RowComponentProps } from 'react-window';
import { makeStyles } from 'tss-react/mui';

import { openEditProductDialog } from './EditProductDialog';
import ResultsBarCharts from '@features/results/ResultsBarCharts';

import { useProductConversionFactorsRecord } from '../use-products.hooks';
import { AUTO_SIZER_STYLES } from '@client/style/style.constants';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { IconButton } from '@ui/buttons';
import { Icon } from '@ui/Icon';
import { Menu } from '@ui/menus/Menu';

const useStyles = makeStyles()({
  listItem: {
    gap: '2rem',
  },
  listItemText: {
    flex: '0 0 35%',
  },
  descriptionTooltip: {
    maxWidth: 600,
  },
});

type SelectProductListProps = {
  products: IProduct[];
  productSelectorIsOpen: boolean;
  selectedUnit: QuantityUnit;
  productId?: string;
  onSelect: (product: IProduct, applyEPD?: boolean) => void;
};

type ContextMenuProps = {
  onEdit: () => void;
  onDuplicate: () => void;
};

const ContextMenu: React.FC<ContextMenuProps> = ({ onEdit, onDuplicate }) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const handleMenuClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      e.preventDefault();
      setMenuAnchor(e.currentTarget);
    },
    [],
  );

  const handleClose = useCallback((): void => {
    setMenuAnchor(null);
  }, []);

  return (
    <>
      <IconButton category="mui" icon="MoreVert" onClick={handleMenuClick} />
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleClose}
      >
        <Menu.Item icon={<Icon category="mui" icon="Edit" />} onClick={onEdit}>
          <FormattedMessage id="product_list.edit" defaultMessage="Edit" />
        </Menu.Item>
        <Menu.Item
          icon={<Icon category="mui" icon="FileCopy" />}
          onClick={onDuplicate}
        >
          <FormattedMessage
            id="product_list.duplicate"
            defaultMessage="Duplicate"
          />
        </Menu.Item>
      </Menu>
    </>
  );
};

export const SelectProductList: React.FC<SelectProductListProps> = ({
  products,
  productSelectorIsOpen,
  selectedUnit,
  productId,
  onSelect,
}) => {
  const { classes } = useStyles();
  const organization = useSelectedOrganizationName();

  const quantityRecord = useProductConversionFactorsRecord(selectedUnit);

  const maxCO2e = useMemo(
    () =>
      Math.max(
        ...products.map((p) =>
          getEPDConversionFactor(p, quantityRecord, 'co2e'),
        ),
      ),
    [products, quantityRecord],
  );

  const maxCost = useMemo(
    () =>
      Math.max(
        ...products.map((p) =>
          getEPDConversionFactor(p, quantityRecord, 'sek'),
        ),
      ),
    [products, quantityRecord],
  );

  const editProduct = useCallback(
    async (product: IProduct, duplicate = false) => {
      const modifiedProduct = await openEditProductDialog({
        product,
        duplicate,
      });
      if (modifiedProduct) {
        onSelect(modifiedProduct);
      }
    },
    [onSelect],
  );
  const displayCurrency = useDisplayCurrency();

  const ProductRenderer = useCallback(
    ({ index, style }: RowComponentProps) => {
      const product = products[index];

      if (!product) return null;
      const co2e = getEPDConversionFactor(product, quantityRecord, 'co2e');
      const cost = getEPDConversionFactor(product, quantityRecord, 'sek');
      const co2eDisplay = getDisplayValueData({ value: co2e, unit: 'CO2e' });
      const costDisplay = getDisplayValueData(
        { value: cost, unit: 'SEK' },
        displayCurrency.isEnabled
          ? { outputUnit: displayCurrency.current }
          : undefined,
      );

      return (
        <ListItemButton
          key={`product-${product.id}`}
          selected={product.id === productId}
          style={style}
          className={classes.listItem}
          onClick={() => onSelect(product, true)}
        >
          <Tooltip
            key={`product-${product.id}`}
            title={product.description}
            placement="bottom-start"
            disableInteractive
            classes={{ tooltip: classes.descriptionTooltip }}
          >
            <ListItemText
              primary={product.name}
              className={classes.listItemText}
            />
          </Tooltip>
          <Tooltip
            title={`${co2eDisplay.full} and ${costDisplay.full} per ${selectedUnit}`}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <ResultsBarCharts
                co2e={co2e}
                cost={cost}
                co2eMax={maxCO2e}
                costMax={maxCost}
              />
            </Box>
          </Tooltip>
          {product.source === ProductSources.Custom &&
            !isReadonlyBySelectedOrganization(organization, product) && (
              <ContextMenu
                onEdit={() => editProduct(product)}
                onDuplicate={() => editProduct(product, true)}
              />
            )}
        </ListItemButton>
      );
    },
    [
      products,
      quantityRecord,
      classes.descriptionTooltip,
      classes.listItem,
      classes.listItemText,
      productId,
      maxCO2e,
      maxCost,
      selectedUnit,
      organization,
      onSelect,
      editProduct,
      displayCurrency,
    ],
  );

  const fixedSizeRenderer: (size: AutoSizerChildProps) => React.ReactElement =
    useCallback(
      ({ height, width }) => {
        return (
          <List
            style={{ height: height || '0', width: width || '0' }}
            rowHeight={55}
            rowCount={products.length}
            rowComponent={ProductRenderer}
            rowProps={{}}
          />
        );
      },
      [ProductRenderer, products],
    );

  if (!productSelectorIsOpen) {
    return null;
  }

  return <AutoSizer style={AUTO_SIZER_STYLES} renderProp={fixedSizeRenderer} />;
};
